import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    BrainCircuit, Loader2, AlertTriangle, Flag, Activity, Users,
    RefreshCcw, PhoneCall, ChevronRight,
} from "lucide-react";
import OrgPatientDetail from "@/components/OrgPatientDetail";
import { SnapshotSections } from "@/pages/SharedUpdateView";

const SORTS = [
    { key: "risk", label: "Highest Risk" },
    { key: "outreach", label: "Needs Outreach" },
    { key: "hospital", label: "New Hospitalization" },
    { key: "burnout", label: "Highest Burnout" },
    { key: "active", label: "Recently Active" },
    { key: "inactive", label: "Recently Inactive" },
];
const RISK_ORDER = { high: 0, moderate: 1, low: 2 };
const RISK_CHIP = {
    high: "bg-coral-light text-coral-dark",
    moderate: "bg-yellow-soft text-yellow-dark",
    low: "bg-stage_early/50 text-stage_early-dark",
};

export default function OrgIntelligence() {
    const { user } = useAuth();
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState(null);
    const [patients, setPatients] = useState(null);
    const [invited, setInvited] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [sort, setSort] = useState("risk");
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                if (user?.role === "admin") {
                    const r = await api.get("/org/admin/list");
                    const list = Array.isArray(r.data) ? r.data : r.data.orgs || [];
                    setOrgs(list);
                    const wanted = new URLSearchParams(window.location.search).get("org");
                    const match = list.find(o => o.id === wanted);
                    setOrgId(match ? match.id : (list[0]?.id || null));
                } else {
                    const r = await api.get("/org/me");
                    const adminOf = r.data?.is_org_admin_of || [];
                    const visible = (r.data?.orgs || []).filter(o => adminOf.includes(o.id));
                    setOrgs(visible);
                    setOrgId(visible[0]?.id || null);
                }
            } catch { setOrgs([]); }
        })();
    }, [user]);

    const load = async () => {
        if (!orgId) return;
        setPatients(null);
        try {
            const [p, a] = await Promise.all([
                api.get(`/org/intel/${orgId}/patients`),
                api.get(`/org/intel/${orgId}/analytics`),
            ]);
            setPatients(p.data.patients || []);
            setInvited(p.data.invited || []);
            setAnalytics(a.data);
        } catch { setPatients([]); }
    };
    useEffect(() => { setOpenId(null); load(); /* eslint-disable-next-line */ }, [orgId]);

    const sorted = useMemo(() => {
        const list = [...(patients || [])];
        const act = p => p.last_activity || "";
        switch (sort) {
            case "risk": return list.sort((a, b) => RISK_ORDER[a.risk.level] - RISK_ORDER[b.risk.level]);
            case "outreach": return list.sort((a, b) => (b.risk.needs_outreach ? 1 : 0) - (a.risk.needs_outreach ? 1 : 0));
            case "hospital": return list.sort((a, b) =>
                (b.alerts.some(x => /Hospital|ER/i.test(x)) ? 1 : 0) - (a.alerts.some(x => /Hospital|ER/i.test(x)) ? 1 : 0));
            case "burnout": return list.sort((a, b) => (b.burnout?.current ?? -1) - (a.burnout?.current ?? -1));
            case "active": return list.sort((a, b) => act(b).localeCompare(act(a)));
            case "inactive": return list.sort((a, b) => act(a).localeCompare(act(b)));
            default: return list;
        }
    }, [patients, sort]);

    const outreachQueue = (patients || []).filter(p => p.risk.needs_outreach);
    const org = orgs.find(o => o.id === orgId);

    if (orgs.length === 0 && user?.role !== "admin") {
        return (
            <div className="max-w-2xl mx-auto py-16 text-center" data-testid="org-intel-none">
                <p className="text-[#4C5551]">You're not an administrator of any organization yet.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in-up" data-testid="org-intel-page">
            <header className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-dark flex items-center gap-1.5">
                            <BrainCircuit size={14} /> Organization Intelligence
                        </p>
                        <h1 className="mt-1 text-2xl md:text-3xl font-heading font-semibold text-[#12776F]">
                            {org?.name || "Your organization"}
                        </h1>
                        <p className="mt-1 text-sm text-[#4C5551] max-w-2xl">
                            The caregiver's story between visits — so your team walks in already knowing.
                            Families own their record; you see what they've shared. Keep documenting in your EHR.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {orgs.length > 1 && (
                            <select value={orgId || ""} onChange={e => setOrgId(e.target.value)} data-testid="org-intel-switch"
                                className="ml-input !py-2 text-sm">
                                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                        )}
                        <button type="button" onClick={load} data-testid="org-intel-refresh"
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-xs font-semibold bg-white border border-cream-dark text-[#4C5551] hover:border-teal">
                            <RefreshCcw size={13} /> Refresh
                        </button>
                        <Link to="/org-admin" className="text-xs text-teal-dark font-medium hover:underline whitespace-nowrap">Pilot admin →</Link>
                    </div>
                </div>

                {analytics && (
                    <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2" data-testid="org-intel-analytics">
                        {[
                            ["Active patients", analytics.active_patients],
                            ["High risk", analytics.high_risk_patients],
                            ["Needs outreach", analytics.needs_outreach],
                            ["Avg burden", analytics.avg_caregiver_burden != null ? `${analytics.avg_caregiver_burden}/10` : "—"],
                            ["AI convos /mo", analytics.monthly_ai_conversations],
                            ["Falls 30d", analytics.falls_30d],
                            ["Hosp. 30d", analytics.hospitalizations_30d],
                            ["Summaries", analytics.clinical_summaries_generated],
                            ["Engagement", analytics.caregiver_engagement_pct != null ? `${analytics.caregiver_engagement_pct}%` : "—"],
                        ].map(([label, val]) => (
                            <div key={label} className="rounded-xl bg-cream/60 px-3 py-2">
                                <p className="text-base font-semibold text-[#1F2624] tabular-nums">{val ?? 0}</p>
                                <p className="text-[10px] text-[#6E7A74]">{label}</p>
                            </div>
                        ))}
                    </div>
                )}
            </header>

            <OrgUpdatesInbox orgId={orgId} />

            {outreachQueue.length > 0 && (
                <section className="ml-card p-5 bg-coral-light/25 border border-coral/30" data-testid="org-intel-outreach-queue">
                    <p className="text-sm font-semibold text-coral-dark flex items-center gap-2">
                        <PhoneCall size={15} /> Needs outreach ({outreachQueue.length})
                    </p>
                    <div className="mt-2.5 flex gap-2 flex-wrap">
                        {outreachQueue.map(p => (
                            <button key={p.user_id} type="button" onClick={() => setOpenId(p.user_id)}
                                data-testid={`outreach-chip-${p.user_id}`}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl bg-white border border-coral/40 text-xs font-semibold text-[#1F2624] hover:border-coral-dark">
                                {p.patient_name} <span className="text-[#6E7A74] font-normal">· {p.risk.reasons[0] || "inactive"}</span>
                                <ChevronRight size={12} />
                            </button>
                        ))}
                    </div>
                </section>
            )}

            <div className="flex gap-1.5 flex-wrap" data-testid="org-intel-sorts">
                {SORTS.map(s => (
                    <button key={s.key} type="button" onClick={() => setSort(s.key)} data-testid={`org-intel-sort-${s.key}`}
                        className={`px-3.5 py-1.5 rounded-2xl text-xs font-semibold transition-colors ${sort === s.key ? "bg-[#1F2624] text-white" : "bg-white border border-cream-dark text-[#4C5551] hover:border-teal"}`}>
                        {s.label}
                    </button>
                ))}
            </div>

            {patients === null ? (
                <div className="ml-card p-10 text-center text-sm text-[#6E7A74]">
                    <Loader2 className="animate-spin inline mr-2" size={15} /> Reading the stories…
                </div>
            ) : sorted.length === 0 ? (
                <div className="ml-card p-10 text-center" data-testid="org-intel-empty">
                    <Users size={22} className="mx-auto text-[#6E7A74]" />
                    <p className="mt-2 text-sm text-[#4C5551]">No families connected yet. Connect families from the Pilot admin page with their Memory Lane code.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="org-intel-patient-grid">
                    {sorted.map(p => (
                        <button key={p.user_id} type="button" onClick={() => setOpenId(p.user_id)}
                            data-testid={`patient-card-${p.user_id}`}
                            className="ml-card p-5 bg-white text-left hover:border-teal transition-colors flex flex-col gap-2.5">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="text-base font-heading font-semibold text-[#1F2624]">{p.patient_name}</p>
                                    <p className="text-xs text-[#6E7A74]">Caregiver: {p.caregiver_name}</p>
                                </div>
                                <span className={`px-2.5 py-1 rounded-2xl text-[11px] font-semibold capitalize ${RISK_CHIP[p.risk.level]}`}>
                                    {p.risk.level} risk
                                </span>
                            </div>
                            <div className="flex gap-x-4 gap-y-1 flex-wrap text-xs text-[#4C5551]">
                                <span>FAST: <strong className="text-[#1F2624]">{p.fast_stage || "—"}</strong>
                                    {p.fast_stage && <span className="text-[#6E7A74]"> (caregiver-reported{p.fast_date ? `, ${p.fast_date}` : ""})</span>}
                                </span>
                                <span>Zarit: <strong className="text-[#1F2624]">{p.zarit ?? "—"}</strong>
                                    {p.zarit != null && <span className="text-[#6E7A74]"> (caregiver-reported{p.zarit_date ? `, ${p.zarit_date}` : ""})</span>}
                                </span>
                                <span>Burnout: <strong className="text-[#1F2624]">{p.burnout?.current != null ? `${p.burnout.current}/10` : "—"}</strong>
                                    {p.burnout?.delta != null && p.burnout.delta !== 0 && (
                                        <span className={p.burnout.delta > 0 ? "text-coral-dark" : "text-teal-dark"}> ({p.burnout.delta > 0 ? "+" : ""}{p.burnout.delta})</span>
                                    )}
                                </span>
                            </div>
                            <div className="flex gap-x-4 gap-y-1 flex-wrap text-[11px] text-[#6E7A74]">
                                <span>Last contact: {p.days_since_contact != null ? `${p.days_since_contact}d ago` : "never"}</span>
                                <span>Caregiver active: {p.days_since_activity != null ? `${p.days_since_activity}d ago` : "—"}</span>
                            </div>
                            {p.alerts.length > 0 && (
                                <div className="flex gap-1.5 flex-wrap">
                                    {p.alerts.slice(0, 4).map((a, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-2xl bg-coral-light/50 text-[11px] font-medium text-coral-dark">
                                            <AlertTriangle size={10} /> {a}
                                        </span>
                                    ))}
                                    {p.alerts.length > 4 && <span className="text-[11px] text-[#6E7A74]">+{p.alerts.length - 4}</span>}
                                </div>
                            )}
                            {p.risk.reasons.length > 0 && (
                                <p className="text-[11px] text-[#6E7A74] inline-flex items-center gap-1">
                                    <Flag size={10} /> {p.risk.reasons[0]}
                                </p>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {invited.length > 0 && (
                <div className="mt-8" data-testid="org-invited-section">
                    <h2 className="text-sm font-heading font-semibold text-[#4C5551] uppercase tracking-wider">
                        Invited — awaiting consent
                    </h2>
                    <p className="text-xs text-[#6E7A74] mt-1">
                        These families were invited but haven&apos;t chosen to share yet. No data is visible
                        until they consent.
                    </p>
                    <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {invited.map((f, i) => (
                            <div key={i} className="rounded-2xl border border-dashed border-[#CBD5E0] bg-white/60 p-4"
                                data-testid={`org-invited-row-${i}`}>
                                <p className="text-sm font-medium text-[#1F2624]">{f.name}</p>
                                <p className="text-[11px] text-[#6E7A74] mt-0.5">
                                    Invited {f.invited_at ? new Date(f.invited_at).toLocaleDateString() : "—"} · awaiting consent
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {orgId && <PilotActivityTile orgId={orgId} />}

            {openId && orgId && (
                <OrgPatientDetail orgId={orgId} userId={openId}
                    orgName={(orgs.find(o => o.id === orgId) || {}).name || "Your organization"}
                    patient={(patients || []).find(p => p.user_id === openId)}
                    onClose={() => setOpenId(null)} onChanged={load} />
            )}

            <p className="text-xs italic text-[#6E7A74] pb-4 flex items-center gap-1.5">
                <Activity size={12} /> Caregiver-reported longitudinal data, shared with your organization by
                the family. Not a medical record system — designed to sit alongside your EHR.
            </p>
        </div>
    );
}

/** Caregiver-pushed updates inbox — one-time shared packages. */
function OrgUpdatesInbox({ orgId }) {
    const [updates, setUpdates] = useState(null);
    const [openDoc, setOpenDoc] = useState(null);

    useEffect(() => {
        if (!orgId) return;
        api.get(`/org/intel/${orgId}/updates`)
            .then(r => setUpdates(r.data.updates || []))
            .catch(() => setUpdates([]));
    }, [orgId]);

    const open = async (id) => {
        try {
            const r = await api.get(`/org/intel/${orgId}/updates/${id}`);
            setOpenDoc(r.data);
            setUpdates(u => (u || []).map(x => x.id === id ? { ...x, viewed: true } : x));
        } catch { /* noop */ }
    };

    if (!updates || updates.length === 0) return null;
    return (
        <section className="ml-card p-5 bg-white border-l-4 border-l-teal" data-testid="org-updates-inbox">
            <p className="text-sm font-semibold text-[#1F2624]">Updates from families ({updates.length})</p>
            <div className="mt-2.5 flex gap-2 flex-wrap">
                {updates.map(u => (
                    <button key={u.id} type="button" onClick={() => open(u.id)} data-testid={`org-update-chip-${u.id}`}
                        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl border text-xs font-semibold transition-colors ${u.viewed ? "bg-white border-cream-dark text-[#4C5551]" : "bg-teal-soft/50 border-teal/40 text-teal-dark"}`}>
                        {!u.viewed && <span className="w-1.5 h-1.5 rounded-full bg-teal" />}
                        {u.caregiver_name} · {u.patient_name}
                        <span className="font-normal text-[#6E7A74]">{new Date(u.created_at).toLocaleDateString()}</span>
                    </button>
                ))}
            </div>
            {openDoc && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="org-update-detail">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setOpenDoc(null)} />
                    <div className="relative w-full max-w-xl bg-cream rounded-3xl shadow-2xl p-6 max-h-[85vh] overflow-y-auto">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="text-base font-heading font-semibold text-[#1F2624]">Update — {openDoc.patient_name}</p>
                                <p className="text-xs text-[#6E7A74]">From {openDoc.caregiver_name} · {new Date(openDoc.created_at).toLocaleString()}</p>
                                {openDoc.note && <p className="mt-2 text-sm italic text-[#1F2624]">“{openDoc.note}”</p>}
                            </div>
                            <button type="button" onClick={() => setOpenDoc(null)} data-testid="org-update-close" className="text-[#6E7A74] hover:text-[#1F2624]">✕</button>
                        </div>
                        <div className="mt-4">
                            <SnapshotSections snapshot={openDoc.snapshot} />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

const PILOT_METRICS = [
    ["exports", "Exports generated"],
    ["contact_notes", "Contact notes logged"],
    ["summaries", "Clinical summaries generated"],
    ["care_plans", "Care plans used"],
    ["prepped_families", "Families with Visit Prep before an appointment"],
];

/* Internal pilot adoption metric — team usage only. Never framed as
 * care-coordination evidence; no CMS language anywhere. */
function PilotActivityTile({ orgId }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/org/intel/${orgId}/pilot-activity`)
            .then(r => setData(r.data))
            .catch(() => setData({ weeks: [], navigators: [] }));
    }, [orgId]);
    if (!data) return null;
    return (
        <section className="mt-8 bg-white border border-cream-dark rounded-3xl p-5 md:p-6" data-testid="pilot-activity-tile">
            <h2 className="font-heading text-base md:text-lg font-semibold text-[#1F2624]">
                Pilot activity — how your team is using Memory Lane
            </h2>
            <p className="text-xs text-[#6E7A74] mt-1" data-testid="pilot-activity-note">
                Activity within Memory Lane. Not a substitute for documentation in your own systems.
            </p>
            {data.navigators.length === 0 ? (
                <p className="text-sm text-[#4C5551] mt-4" data-testid="pilot-activity-empty">
                    No team activity in the last 4 weeks yet.
                </p>
            ) : data.navigators.map(nav => (
                <div key={nav.name} className="mt-5" data-testid={`pilot-activity-nav-${nav.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="text-sm font-semibold text-[#1F2624]">{nav.name}</p>
                    <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-xs min-w-[480px]">
                            <thead>
                                <tr className="text-[#6E7A74]">
                                    <th className="text-left font-medium py-1.5 pr-3">Per week</th>
                                    {data.weeks.map(w => (
                                        <th key={w} className="text-center font-medium py-1.5 px-2 whitespace-nowrap">Wk of {w}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {PILOT_METRICS.map(([k, label]) => (
                                    <tr key={k} className="border-t border-cream-dark/60">
                                        <td className="py-1.5 pr-3 text-[#4C5551]">{label}</td>
                                        {data.weeks.map(w => (
                                            <td key={w} className="text-center py-1.5 px-2 text-[#1F2624] font-medium">
                                                {(nav.weekly?.[w] || {})[k] || 0}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </section>
    );
}
